import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { auth } from '@/config/firebase';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { StartupLogoUpload } from '@/components/startup-logo-upload';
import { useToast } from '@/hooks/use-toast';

import { uploadService } from '@/services/upload';

export function RegisterStartupPage() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const { toast } = useToast();

    // Logo State
    const [logoFile, setLogoFile] = useState<File | null>(null);

    // Avatar State
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
    const avatarInputRef = useRef<HTMLInputElement>(null);

    // Form State
    const [startupName, setStartupName] = useState('');
    const [email, setEmail] = useState('');

    const [password, setPassword] = useState('');
    const [website, setWebsite] = useState('');
    const [industry, setIndustry] = useState('');
    const [foundedDate, setFoundedDate] = useState('');
    const [problem, setProblem] = useState('');
    const [solution, setSolution] = useState('');
    const [fullName, setFullName] = useState('');
    const [title, setTitle] = useState('');

    // Validation errors
    const [errors, setErrors] = useState<Record<string, string>>({});

    // --- Avatar Handlers ---
    const handleAvatarClick = () => {
        avatarInputRef.current?.click();
    };

    const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setAvatarFile(file);
            setAvatarPreview(URL.createObjectURL(file));
        }
    };

    const removeAvatar = (e: React.MouseEvent) => {
        e.stopPropagation();
        setAvatarFile(null);
        if (avatarPreview) URL.revokeObjectURL(avatarPreview);
        setAvatarPreview(null);
        if (avatarInputRef.current) avatarInputRef.current.value = '';
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!fullName.trim()) newErrors.fullName = 'Name is required';
        if (!title.trim()) newErrors.title = 'Title is required';
        if (!email.trim()) newErrors.email = 'Email is required';
        if (!password.trim()) newErrors.password = 'Password is required';
        else if (password.length < 6) newErrors.password = 'Password must be at least 6 characters';
        if (!startupName.trim()) newErrors.startupName = 'Startup name is required';
        if (!website.trim()) newErrors.website = 'Website URL is required';
        if (!industry) newErrors.industry = 'Industry is required';
        if (!foundedDate.trim()) newErrors.foundedDate = 'Founded date is required';
        else if (!/^(0[1-9]|1[0-2])-\d{4}$/.test(foundedDate)) newErrors.foundedDate = 'Use MM-YYYY format (e.g. 12-2023)';
        if (!problem.trim()) newErrors.problem = 'Problem statement is required';
        if (!solution.trim()) newErrors.solution = 'Solution is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    return (
        <div className="bg-unicorn-mesh-light dark:bg-none dark:bg-background-dark text-slate-900 dark:text-white overflow-x-hidden antialiased transition-colors duration-300 min-h-screen relative flex flex-col font-sans">
            <style>{`
                .material-symbols-outlined { font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24; }
                .filled-icon { font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24; }
                ::-webkit-scrollbar { width: 8px; }
                ::-webkit-scrollbar-track { background: transparent; }
                ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
                ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .dark ::-webkit-scrollbar-thumb { background: #334155; }
                .dark ::-webkit-scrollbar-thumb:hover { background: #475569; }
                .glass-card {
                    background: rgba(255, 255, 255, 0.7);
                    backdrop-filter: blur(10px);
                    -webkit-backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.5);
                }
                .dark .glass-card {
                    background: rgba(15, 23, 42, 0.6);
                    border: 1px solid rgba(255, 255, 255, 0.05);
                }
                .form-input {
                    transition: all 0.2s ease;
                }
                .form-input:focus {
                    box-shadow: 0 0 0 4px rgba(79, 70, 229, 0.1);
                }
                .logo-gradient-text {
                    background: linear-gradient(to right, #3B52D0, #4194F2, #A378CF, #F0766E);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }
            `}</style>
            <div className="fixed inset-0 z-[-1] opacity-0 dark:opacity-40 bg-unicorn-mesh pointer-events-none transition-opacity duration-500"></div>

            <Navbar variant="minimal" />

            <main className="flex-1 w-full max-w-[1920px] mx-auto px-4 md:px-8 py-4 md:py-6 relative z-10">
                <div className="mb-6 text-center relative py-6 px-6 rounded-3xl glass-card overflow-hidden shadow-lg">
                    <div className="absolute inset-0 bg-logo-gradient opacity-5"></div>
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#4f46e5]/20 to-transparent"></div>
                    <h1 className="relative z-10 bg-logo-gradient bg-clip-text text-transparent text-4xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-0 pb-2 drop-shadow-sm font-display">Startup Registration</h1>
                </div>

                <div className="flex flex-col gap-8">
                    {/* 1. Personal Details - Full Width */}
                    <div className="rounded-3xl overflow-hidden glass-card shadow-lg border border-white/50 dark:border-slate-700">
                        <div className="bg-logo-gradient p-5 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="material-symbols-outlined text-white text-3xl">person</span>
                                <h2 className="text-white text-xl font-extrabold leading-tight font-display">Personal Details</h2>
                            </div>
                        </div>
                        <div className="p-6 md:p-8">
                            <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_1fr] gap-6 items-start">
                                {/* Avatar Upload */}
                                <div className="flex justify-center md:row-span-2">
                                    <div
                                        className="group relative flex flex-col items-center gap-2 cursor-pointer"
                                        onClick={handleAvatarClick}
                                    >
                                        <div className="w-28 h-28 rounded-full bg-white dark:bg-slate-800 border-4 border-dashed border-indigo-200 dark:border-slate-600 flex items-center justify-center group-hover:border-[#4f46e5] transition-colors relative shadow-sm overflow-hidden">
                                            {avatarPreview ? (
                                                <>
                                                    <img src={avatarPreview} alt="Avatar Preview" className="w-full h-full object-cover" />
                                                    <button
                                                        onClick={removeAvatar}
                                                        className="absolute top-1 right-1 p-1 bg-white dark:bg-slate-800 text-slate-500 hover:text-red-500 rounded-full shadow-lg z-20 border border-slate-100 dark:border-slate-700"
                                                    >
                                                        <span className="material-symbols-outlined text-xs font-bold">close</span>
                                                    </button>
                                                </>
                                            ) : (
                                                <div className="flex flex-col items-center text-slate-400 group-hover:text-[#4f46e5] transition-colors">
                                                    <span className="material-symbols-outlined text-3xl">add_a_photo</span>
                                                    <span className="text-[10px] font-bold mt-1 uppercase tracking-wide">Photo</span>
                                                </div>
                                            )}
                                            <input
                                                ref={avatarInputRef}
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={handleAvatarChange}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-slate-700 dark:text-slate-300 text-sm font-bold ml-1">Your Name <span className="text-red-500">*</span></label>
                                    <input
                                        value={fullName}
                                        onChange={(e) => { setFullName(e.target.value); if (errors.fullName) setErrors(prev => ({ ...prev, fullName: '' })); }}
                                        className={`w-full h-11 rounded-lg border ${errors.fullName ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'} bg-white/50 dark:bg-slate-900/50 dark:text-white px-4 text-base focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-600 shadow-sm`} placeholder="e.g. Jane Doe" type="text"
                                    />
                                    {errors.fullName && <p className="text-sm text-red-500 mt-1 font-medium">{errors.fullName}</p>}
                                </div>
                                <div className="space-y-2">
                                    <label className="text-slate-700 dark:text-slate-300 text-sm font-bold ml-1">Your Title <span className="text-red-500">*</span></label>
                                    <input
                                        value={title}
                                        onChange={(e) => { setTitle(e.target.value); if (errors.title) setErrors(prev => ({ ...prev, title: '' })); }}
                                        className={`w-full h-11 rounded-lg border ${errors.title ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'} bg-white/50 dark:bg-slate-900/50 dark:text-white px-4 text-base focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-600 shadow-sm`} placeholder="e.g. Founder & CEO" type="text"
                                    />
                                    {errors.title && <p className="text-sm text-red-500 mt-1 font-medium">{errors.title}</p>}
                                </div>
                                <div className="space-y-2 md:col-start-2">
                                    <label className="text-slate-700 dark:text-slate-300 text-sm font-bold ml-1">Email <span className="text-red-500">*</span></label>
                                    <input
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
                                        }}
                                        className={`w-full h-11 rounded-lg border ${errors.email ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'} bg-white/50 dark:bg-slate-900/50 dark:text-white px-4 text-base focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-600 shadow-sm`} type="email"
                                    />
                                    {errors.email && <p className="text-sm text-red-500 mt-1 font-medium">{errors.email}</p>}
                                </div>
                                <div className="space-y-2">
                                    <label className="text-slate-700 dark:text-slate-300 text-sm font-bold ml-1">Password <span className="text-red-500">*</span></label>
                                    <input
                                        value={password}
                                        onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors(prev => ({ ...prev, password: '' })); }}
                                        className={`w-full h-11 rounded-lg border ${errors.password ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'} bg-white/50 dark:bg-slate-900/50 dark:text-white px-4 text-base focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-600 shadow-sm`} type="password"
                                    />
                                    {errors.password && <p className="text-sm text-red-500 mt-1 font-medium">{errors.password}</p>}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Row: Company Info (left) + Proposition (right) */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                        {/* 2. Company Info */}
                        <div className="rounded-3xl overflow-hidden glass-card shadow-lg border border-white/50 dark:border-slate-700">
                            <div className="bg-logo-gradient p-5 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <span className="material-symbols-outlined text-white text-3xl">business</span>
                                    <h2 className="text-white text-xl font-extrabold leading-tight font-display">Company Info</h2>
                                </div>
                            </div>
                            <div className="p-6 md:p-8">
                                <div className="flex flex-col gap-6">
                                    <div className="space-y-2">
                                        <label className="text-slate-700 dark:text-slate-300 text-sm font-bold ml-1">Company Logo</label>
                                        <div className="p-4 rounded-xl bg-white/40 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700">
                                            <StartupLogoUpload
                                                onFileSelect={setLogoFile}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-slate-700 dark:text-slate-300 text-sm font-bold ml-1">Startup Name <span className="text-red-500">*</span></label>
                                        <input
                                            value={startupName}
                                            onChange={(e) => { setStartupName(e.target.value); if (errors.startupName) setErrors(prev => ({ ...prev, startupName: '' })); }}
                                            className={`w-full h-11 rounded-lg border ${errors.startupName ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'} bg-white/50 dark:bg-slate-900/50 dark:text-white px-4 text-base focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-600 shadow-sm`} placeholder="e.g. My Startup" type="text"
                                        />
                                        {errors.startupName && <p className="text-sm text-red-500 mt-1 font-medium">{errors.startupName}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-slate-700 dark:text-slate-300 text-sm font-bold ml-1">Website URL <span className="text-red-500">*</span></label>
                                        <input
                                            value={website}
                                            onChange={(e) => { setWebsite(e.target.value); if (errors.website) setErrors(prev => ({ ...prev, website: '' })); }}
                                            className={`w-full h-11 rounded-lg border ${errors.website ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'} bg-white/50 dark:bg-slate-900/50 dark:text-white px-4 text-base focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-600 shadow-sm`} placeholder="e.g. https://mystartup.com" type="url"
                                        />
                                        {errors.website && <p className="text-sm text-red-500 mt-1 font-medium">{errors.website}</p>}
                                    </div>
                                    <div className="grid grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label className="text-slate-700 dark:text-slate-300 text-sm font-bold ml-1">Industry <span className="text-red-500">*</span></label>
                                            <div className="relative">
                                                <select
                                                    value={industry}
                                                    onChange={(e) => { setIndustry(e.target.value); if (errors.industry) setErrors(prev => ({ ...prev, industry: '' })); }}
                                                    className={`w-full h-11 rounded-lg border ${errors.industry ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'} bg-white/50 dark:bg-slate-900/50 dark:text-white px-4 text-base focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] outline-none appearance-none cursor-pointer shadow-sm`}>
                                                    <option value="">Select Industry</option>
                                                    <option value="fintech">Fintech</option>
                                                    <option value="healthtech">Healthtech</option>
                                                    <option value="saas">SaaS</option>
                                                    <option value="ai">AI/ML</option>
                                                    <option value="ecommerce">E-commerce</option>
                                                    <option value="edtech">EdTech</option>
                                                    <option value="enterprise_software">Enterprise Software</option>
                                                    <option value="consumer_apps">Consumer Apps</option>
                                                    <option value="blockchain">Blockchain</option>
                                                    <option value="cybersecurity">Cybersecurity</option>
                                                    <option value="climate_tech">Climate Tech</option>
                                                    <option value="biotech">BioTech</option>
                                                    <option value="hardware">Hardware</option>
                                                    <option value="other">Other</option>
                                                </select>
                                                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                                                    <span className="material-symbols-outlined text-slate-500 text-sm">expand_more</span>
                                                </div>
                                            </div>
                                            {errors.industry && <p className="text-sm text-red-500 mt-1 font-medium">{errors.industry}</p>}
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-slate-700 dark:text-slate-300 text-sm font-bold ml-1">Founded Date <span className="text-red-500">*</span></label>
                                            <input
                                                value={foundedDate}
                                                onChange={(e) => { setFoundedDate(e.target.value); if (errors.foundedDate) setErrors(prev => ({ ...prev, foundedDate: '' })); }}
                                                placeholder="MM-YYYY"
                                                className={`w-full h-11 rounded-lg border ${errors.foundedDate ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'} bg-white/50 dark:bg-slate-900/50 dark:text-white px-4 text-base focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-600 shadow-sm`}
                                                type="text"
                                                maxLength={7}
                                            />
                                            {errors.foundedDate && <p className="text-sm text-red-500 mt-1 font-medium">{errors.foundedDate}</p>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 3. Proposition */}
                        <div className="rounded-3xl overflow-hidden glass-card shadow-lg border border-white/50 dark:border-slate-700 h-full flex flex-col">
                            <div className="bg-logo-gradient p-5 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <span className="material-symbols-outlined text-white text-3xl">lightbulb</span>
                                    <h2 className="text-white text-xl font-extrabold leading-tight font-display">Proposition</h2>
                                </div>
                            </div>

                            <div className="p-6 md:p-8 space-y-6 flex-1 flex flex-col">
                                <div className="space-y-2 flex-1 flex flex-col">
                                    <label className="text-slate-700 dark:text-slate-300 text-sm font-bold ml-1">Problem Statement <span className="text-red-500">*</span></label>
                                    <textarea
                                        value={problem}
                                        onChange={(e) => { setProblem(e.target.value); if (errors.problem) setErrors(prev => ({ ...prev, problem: '' })); }}
                                        className={`w-full rounded-lg border ${errors.problem ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'} bg-white/50 dark:bg-slate-900/50 dark:text-white p-4 min-h-[120px] flex-1 focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500 resize-none shadow-sm`} placeholder="Describe the problem you are solving..."
                                    />
                                    {errors.problem && <p className="text-sm text-red-500 mt-1 font-medium">{errors.problem}</p>}
                                </div>
                                <div className="space-y-2 flex-1 flex flex-col">
                                    <label className="text-slate-700 dark:text-slate-300 text-sm font-bold ml-1">Solution <span className="text-red-500">*</span></label>
                                    <textarea
                                        value={solution}
                                        onChange={(e) => { setSolution(e.target.value); if (errors.solution) setErrors(prev => ({ ...prev, solution: '' })); }}
                                        className={`w-full rounded-lg border ${errors.solution ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'} bg-white/50 dark:bg-slate-900/50 dark:text-white p-4 min-h-[120px] flex-1 focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500 resize-none shadow-sm`} placeholder="Describe your solution..."
                                    />
                                    {errors.solution && <p className="text-sm text-red-500 mt-1 font-medium">{errors.solution}</p>}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Info banner about documents */}
                    <div className="rounded-2xl p-4 bg-indigo-50/80 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800/50 flex items-start gap-3">
                        <span className="material-symbols-outlined text-indigo-500 dark:text-indigo-400 text-xl mt-0.5 shrink-0">info</span>
                        <div>
                            <p className="text-sm font-bold text-indigo-900 dark:text-indigo-300">Documents & Video Pitch</p>
                            <p className="text-xs text-indigo-700 dark:text-indigo-400 mt-0.5">You can upload your pitch deck, supporting documents, and video pitch after registration and email verification.</p>
                        </div>
                    </div>

                    <div className="flex justify-end pt-2 pb-8">
                        <div className="flex flex-col items-center md:items-end gap-2 text-center md:text-right">
                            <button
                                onClick={async () => {
                                    try {
                                        // Validate all required fields
                                        if (!validateForm()) {
                                            toast({
                                                title: "Missing required fields",
                                                description: "Please fill in all required fields before submitting.",
                                                variant: "destructive"
                                            });
                                            return;
                                        }



                                        setIsLoading(true);

                                        // 1. Create Firebase User
                                        const userCred = await createUserWithEmailAndPassword(auth, email, password);
                                        const uid = userCred.user.uid;

                                        // 2. Send Email Verification
                                        try {
                                            const actionCodeSettings = {
                                                url: 'https://uniscout.ai/dashboard',
                                                handleCodeInApp: true,
                                            };
                                            await sendEmailVerification(userCred.user, actionCodeSettings);
                                        } catch (err) {
                                            console.error("Failed to send verification email:", err);
                                        }

                                        // 3. Upload Avatar if selected
                                        let avatarUrl = null;
                                        if (avatarFile) {
                                            try {
                                                const res = await uploadService.uploadFile(avatarFile, 'image');
                                                avatarUrl = res.url;
                                            } catch (err) {
                                                console.error("Avatar upload failed", err);
                                            }
                                        }

                                        // 4. Upload Logo if selected
                                        let logoUrl = null;
                                        if (logoFile) {
                                            try {
                                                const res = await uploadService.uploadFile(logoFile, 'image');
                                                logoUrl = res.url;
                                            } catch (err) {
                                                console.error("Logo upload failed", err);
                                            }
                                        }

                                        // Convert MM-YYYY to YYYY-MM-01
                                        let foundedDateIso = foundedDate;
                                        if (foundedDate && /^\d{2}-\d{4}$/.test(foundedDate)) {
                                            const [month, year] = foundedDate.split('-');
                                            foundedDateIso = `${year}-${month}-01`;
                                        }

                                        // 5. Register on Backend
                                        await uploadService.registerUser({
                                            id: uid,
                                            email: email,
                                            full_name: fullName,
                                            job_title: title,
                                            role: 'startup',
                                            organization_name: startupName,
                                            org_logo_url: logoUrl,
                                            avatar_url: avatarUrl,
                                            startup_profile: {
                                                website_url: website,
                                                industry: industry,
                                                founded_date: foundedDateIso,
                                                problem_statement: problem,
                                                solution: solution,
                                                documents: [],
                                                video_pitch_url: null
                                            }
                                        });

                                        // 6. Redirect to email verification page
                                        navigate('/auth/verify-email', { state: { email } });

                                    } catch (e: unknown) {
                                        const message = e instanceof Error ? e.message : "Registration failed";
                                        alert(message);
                                    } finally {
                                        setIsLoading(false);
                                    }
                                }}
                                disabled={isLoading}
                                className="w-full md:w-auto px-10 py-4 rounded-xl bg-logo-gradient hover:shadow-lg hover:shadow-[#4f46e5]/30 text-white text-base font-bold transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed">
                                {isLoading ? 'Creating Application...' : 'Register'}
                                {!isLoading && <span className="material-symbols-outlined mb-0.5">send</span>}
                            </button>
                            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium px-2">Upload documents & video pitch after verification</p>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
